import React, { useState, useEffect, useRef } from 'react';

import DataTableHeader from './header/header';
import DataTableItem from './item/item';
import Spinner from 'app/shared/spinners/spinner-15/spinner-15';
import SplitButton from 'app/shared/button/split';

import '../datatable.css';
import { IconInput } from 'app/shared/input';
import { Button } from 'app/shared/button';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'app/hooks/query';
import { TableFilter } from 'app/shared/filter';
import { toReadableNumber } from 'app/shared/utils/number';

const DataTable = ({
  showHeader = true,
  showFooter = true,
  allowed_empty_bulk_actions = [],
  action,
  checkbox,
  config,
  filter_config = [],
  has_filter = false,
  header_tabs = null,
  loading_data,
  resetBulkSelection,
  reset_key,
  table_actions = [],
  onClick = () => {},
  onDataRequest = (f) => f,
  onSearchRequest = (f) => f
}) => {
  const { fields, items, num_of_rows = 10, search_text, total_count } = config;
  const base_path = window.location.pathname;
  const query = useQuery();
  const navigateTo = useNavigate();

  const [filter_query, setFilterQuery] = useState('');
  const [sort_query, setSortQuery] = useState('');

  // Single source of truth for page size: used for the request, the footer
  // count, and pagination. (Previously requests hard-coded 100 while the
  // footer/slice used num_of_rows, so they disagreed.) Now user-adjustable via
  // the row-count control in the footer.
  const [number_of_rows_to_display, setNumberOfRowsToDisplay] = useState(num_of_rows);
  const [page_number, setPageNumber] = useState(0);

  const [selected_items, setSelectedItems] = useState({});
  const [search_keyword, setSearchKeyword] = useState('');
  const [search_keys, setSearchKeys] = useState('');
  const [show_filters, setShowFilters] = useState(false);
  const [is_selected, setIsSelected] = useState(false);

  // The current page of rows, exactly as returned by the server.
  const [table_items, setTableItems] = useState([]);
  const debounce_timer = useRef(null);
  // Skip the initial paginate/search effect run on mount (the mount effect
  // already issues the first request).
  const is_first_render = useRef(true);
  // Keyword restored from the URL on mount. Its first keyword change must not
  // trigger the debounced search (which resets to page 0 and loses ?page=).
  const hydrated_keyword = useRef(null);

  useEffect(() => {
    const keys = buildSearchKeys();

    const { page, q } = query;
    const initial_page = page ? Number(page) : 0;
    if (q) {
      const keyword = decodeURIComponent(q);
      hydrated_keyword.current = keyword;
      setSearchKeyword(keyword);
      setPageNumber(initial_page);
      onSearchRequest(keys, keyword, initial_page, number_of_rows_to_display, {
        filter_query,
        sort_query
      });
    } else {
      makeRequests(initial_page);
      if (initial_page) setPageNumber(initial_page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resetBulkSelection) setSelectedItems({});
  }, [resetBulkSelection]);

  // Debounce search input, then reset to page 0 and request.
  useEffect(() => {
    if (is_first_render.current) return;
    if (hydrated_keyword.current !== null) {
      const is_hydration = search_keyword === hydrated_keyword.current;
      hydrated_keyword.current = null;
      if (is_hydration) return;
    }
    if (debounce_timer.current) clearTimeout(debounce_timer.current);
    debounce_timer.current = setTimeout(() => {
      setPageNumber(0);
      makeRequests(0);
    }, 250);
    return () => clearTimeout(debounce_timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search_keyword]);

  // Keep the URL query string in sync with page + search keyword.
  useEffect(() => {
    if (is_first_render.current) return;
    const params = [];
    if (page_number) params.push(`page=${page_number}`);
    if (search_keyword) params.push(`q=${encodeURIComponent(search_keyword)}`);
    navigateTo(`${base_path}?${params.join('&')}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page_number, search_keyword]);

  // Changing filter or sort resets to the first page and re-requests.
  useEffect(() => {
    if (is_first_render.current) return;
    setPageNumber(0);
    makeRequests(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter_query, sort_query]);

  // Parent-driven reset (e.g. header_tabs switching which list is shown): back
  // to page 1 with no selection, re-requested through the new props.
  useEffect(() => {
    if (is_first_render.current) return;
    setSelectedItems({});
    setPageNumber(0);
    makeRequests(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset_key]);

  // Declared after every effect guarded by is_first_render, so all of them skip
  // the mount run (effects run in declaration order).
  useEffect(() => {
    is_first_render.current = false;
  }, []);

  useEffect(() => {
    // Render the current page's rows. Cap to the page size so the table never
    // shows more rows than expected even if the API returns extra items.
    setTableItems(() => items.slice(0, number_of_rows_to_display));
  }, [items, number_of_rows_to_display]);

  const buildSearchKeys = () => {
    const keys = [];
    fields.forEach((field) => {
      if (!field.isTitle && !field.isTagline) return;
      keys.push(field.key);
    });
    const joined = keys.join();
    setSearchKeys(joined);
    return joined;
  };

  /** */
  const closeActionMode = () => {
    setSelectedItems(() => ({}));
  };

  const formatDataTableFooter = () => {
    const start = page_number ? page_number * number_of_rows_to_display + 1 : 1;
    const end = number_of_rows_to_display + start - 1;
    const content_text_for_empty_data = 'No item(s) to display';
    const content_text_for_nonempty_data = `${start} - ${
      end > total_count ? total_count : end
    } of ${toReadableNumber(total_count)}`;

    return table_items.length ? content_text_for_nonempty_data : content_text_for_empty_data;
  };

  const formatPagination = () => {
    const high = Math.ceil(total_count / number_of_rows_to_display);
    return (
      <div className="gm-datatable-pagination">
        {loading_data ? (
          <div className="gm-datatable-loader-indicator">
            <Spinner /> <span>Loading</span>
          </div>
        ) : (
          <></>
        )}
        {page_number > 0 ? (
          <button onClick={() => handlePageChange(-1)}> {'Prev'} </button>
        ) : (
          <button disabled> {'Prev'} </button>
        )}
        <span className="gm-datatable-row-selector">
          <select
            value={number_of_rows_to_display}
            onChange={handlePageSizeChange}
            aria-label="Rows per page"
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </span>
        {page_number < high - 1 ? (
          <button onClick={() => handlePageChange(1)}> {'Next'} </button>
        ) : (
          <button disabled> {'Next'} </button>
        )}
      </div>
    );
  };

  const handleItemClick = (item_data) => {
    onClick(item_data);
  };

  const handlePageChange = (value) => {
    const new_page_num = Number(page_number) + Number(value);
    setPageNumber(() => new_page_num);

    makeRequests(new_page_num);
  };

  const handlePageSizeChange = (event) => {
    const new_size = Number(event.target.value);
    if (!new_size || new_size === number_of_rows_to_display) return;
    setNumberOfRowsToDisplay(new_size);
    setPageNumber(0);
    makeRequests(0, new_size);
  };

  const handleSearch = async (keyword) => {
    setSearchKeyword(keyword);
  };

  const handleSelection = (selected_item) => {
    setSelectedItems((selected_items) => ({
      ...selected_items,
      [selected_item.id]: selected_item
    }));
  };

  const handleUnselection = (id) => {
    const selections = { ...selected_items };
    delete selections[id];
    setSelectedItems(() => selections);
  };

  const handleSort = (field, is_ascending) => {
    setSortQuery(`sort_by=${!is_ascending ? '-' : ''}${field}`);
  };

  const makeRequests = async (page_num = 0, page_size = number_of_rows_to_display) => {
    const reset = filter_query === '&';
    if (search_keyword)
      onSearchRequest(search_keys, search_keyword, page_num, page_size, {
        filter_query,
        sort_query,
        reset
      });
    else onDataRequest(page_num, page_size, { filter_query, sort_query, reset });
  };

  const processAction = async (name, data) => {
    if (data && data.id) {
      // it is a single action
      action({
        name,
        type: 'single',
        data
      });
      return;
    }

    if (!Object.keys(selected_items).length && !allowed_empty_bulk_actions.includes(name)) return;

    action({
      name,
      type: 'bulk',
      data: Object.values(selected_items)
    });
  };

  const toggleBulkSelection = () => {
    // If anything is selected, clear it all; otherwise select the whole page.
    // (Previously this only cleared when every row was selected, so clicking
    // the header checkbox with a partial selection did nothing useful.)
    if (Object.keys(selected_items).length > 0) {
      closeActionMode();
      return;
    }

    // Select every row on the current page. Skip only rows explicitly marked
    // inactive — contacts don't always carry is_active, so a truthy check would
    // wrongly drop every row and select nothing.
    const selections = table_items.reduce((sack, result) => {
      if (result.is_active === false) return sack;
      return {
        ...sack,
        [result.id]: result
      };
    }, {});
    setSelectedItems(() => selections);
  };

  const checkBulkMode = () => {
    return table_items.length > 0 && Object.keys(selected_items).length === table_items.length;
  };

  // Count only selections that are on the current page, so selections left over
  // from another page don't inflate the displayed count.
  const selected_in_view = table_items.reduce(
    (count, item) => (selected_items[item.id] ? count + 1 : count),
    0
  );

  return (
    <div className="gm-datatable">
      {showHeader && (
        <section className="gm-datatable-header">
          <div className={`gm-datatable-search${header_tabs ? ' with-tabs' : ''}`}>
            {header_tabs}
            {!selected_in_view && (
              <IconInput
                icon_name="search"
                placeholder={search_text || 'Search'}
                onInput={(value) => handleSearch(value)}
                value={search_keyword}
                wrapperClassName="search-input"
              />
            )}
            {selected_in_view > 0 && (
              <span className="gm-datatable-selected-count">{selected_in_view} selected</span>
            )}
          </div>
          <div className="gm-datatable-actions">
            {has_filter && !selected_in_view && (
              <Button
                type="secondary"
                icon_name="filter"
                text="Filter"
                className="gm-datatable-filter-btn"
                onClick={() => setShowFilters(true)}
              />
            )}
            {table_actions}
            <SplitButton
              actions={config.actions.bulk}
              callback={processAction}
              disabled={!config.actions.bulk}
              type="secondary"
              hide={!selected_in_view}
            />
          </div>
        </section>
      )}
      <section className="gm-datatable-table">
        <table>
          <thead>
            <DataTableHeader
              actions={config.actions.bulk}
              checkbox={checkbox}
              data={fields}
              sort_callback={handleSort}
              onChange={toggleBulkSelection}
              unselect={resetBulkSelection}
              is_selected={is_selected}
              setIsSelected={setIsSelected}
              is_bulk_mode={checkBulkMode()}
              show_action_column={!!config.actions.single}
            />
          </thead>
          <tbody>
            {table_items.map((item) => (
              <DataTableItem
                actions={config.actions.single}
                action_callback={processAction}
                checkbox={config.actions.bulk}
                data={item}
                fields={fields}
                index={item.id}
                item_click_callback={handleItemClick}
                key={item.id}
                bulk_selection={selected_items}
                selection_callback={handleSelection}
                unselection_callback={handleUnselection}
                deselect={!Object.keys(selected_items).length}
              />
            ))}
          </tbody>
        </table>
        {loading_data && <div className="gm-datatable-full-loader">loading data</div>}
      </section>
      {showFooter && (
        <section className="gm-datatable-footer">
          <div>{formatDataTableFooter()}</div>
          {formatPagination()}
        </section>
      )}
      <TableFilter
        config={filter_config}
        is_open={show_filters}
        onApply={setFilterQuery}
        onClose={() => setShowFilters(false)}
      />
    </div>
  );
};

export default DataTable;
